import { useEffect, useState } from 'react';
import { CheckCircle2, Clock3, Coins, Copy, FolderPlus, Frame, Image, Layers3, LogOut, MessageSquareText, MonitorUp, Save, Sparkles, Trash2 } from 'lucide-react';
import Dialog from './Dialog.jsx';
import { api } from '../lib/api.js';

export function RechargeDialog({ open, onClose, onBalanceRefresh, onNotify }) {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState('points-50');
  const [type, setType] = useState('alipay');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setError('');
    api.paymentPackages().then((result) => { if (alive) setPackages(result.packages || []); }).catch((cause) => { if (alive) setError(cause.message); });
    return () => { alive = false; };
  }, [open]);
  useEffect(() => {
    if (!open || !order || order.status !== 'pending') return undefined;
    const timer = window.setInterval(async () => {
      try {
        const result = await api.paymentOrder(order.id);
        setOrder(result.order);
        if (result.order?.status === 'paid') { onBalanceRefresh?.(); onNotify?.('充值成功，积分已到账。'); }
      } catch { /* Keep the order visible; callback or manual refresh can settle it. */ }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [open, order, order?.id, order?.status, onBalanceRefresh, onNotify]);
  const create = async () => {
    setLoading(true); setError('');
    try {
      const result = await api.createPaymentOrder(selected, type, `recharge-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`}`);
      setOrder(result.order);
      if (result.order?.payType === 'jump' && result.order.payInfo) window.open(result.order.payInfo, '_blank', 'noopener,noreferrer');
    } catch (cause) { setError(cause.message); }
    finally { setLoading(false); }
  };
  const chosen = packages.find((item) => item.id === selected);
  return <Dialog open={open} onClose={onClose} title="充值积分" description="微信和支付宝支付，支付成功后积分自动到账。">
    {error && <p className="dialog-error" role="alert">{error}</p>}
    {!order ? <>
      <div className="recharge-packages">{packages.map((item) => <button type="button" key={item.id} className={selected === item.id ? 'is-selected' : ''} onClick={() => setSelected(item.id)}><strong>￥{item.amount}</strong><span>{item.points} 积分</span></button>)}</div>
      <div className="recharge-types" role="group" aria-label="支付方式"><button type="button" className={type === 'alipay' ? 'is-selected' : ''} onClick={() => setType('alipay')}>支付宝</button><button type="button" className={type === 'wxpay' ? 'is-selected' : ''} onClick={() => setType('wxpay')}>微信支付</button></div>
      <button className="primary-button" type="button" disabled={loading || !chosen} onClick={create}>{loading ? '正在创建订单…' : `支付 ￥${chosen?.amount || 0}`}</button>
      <small className="recharge-note">不支持退款。请确认套餐和金额后再支付。</small>
    </> : <div className="recharge-order-state"><strong>{order.status === 'paid' ? '充值成功' : order.status === 'failed' ? '订单创建失败' : '等待支付'}</strong><span>订单：{order.outTradeNo}</span><span>金额：￥{Number(order.amountFen || 0) / 100} · {order.points} 积分</span>{order.payType === 'qrcode' && order.payInfo && <img className="recharge-qr" src={order.payInfo} alt="支付二维码" />}{order.payType === 'jump' && order.payInfo && <a className="primary-button" href={order.payInfo} target="_blank" rel="noreferrer">打开支付页面</a>}<button className="secondary-button" type="button" onClick={() => setOrder(null)}>返回套餐</button></div>}
  </Dialog>;
}

export function ContactDialog({ open, onClose, config }) {
  const [copied, setCopied] = useState(false);
  const contact = config?.adminContact || 'support@example.com';
  const copy = async () => {
    await navigator.clipboard.writeText(contact);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <Dialog open={open} onClose={onClose} className="dialog-backdrop--anchored" title="联系管理员" description="充值、订单或生成问题可通过以下方式联系。">
      <div className="contact-layout">
        <div className="contact-qr">
          {config?.contactQr ? <img src={config.contactQr} alt="管理员联系二维码" /> : <MessageSquareText size={38} />}
        </div>
        <div className="contact-details">
          <span>管理员联系方式</span>
          <strong>{contact}</strong>
          <button type="button" onClick={copy}>{copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}{copied ? '已复制' : '复制联系方式'}</button>
          <small>工作时间内通常会更快回复</small>
        </div>
      </div>
    </Dialog>
  );
}

export function SettingsDialog({ open, onClose, user, onSave, onLogout, busy }) {
  const [passwords, setPasswords] = useState({ oldPassword: '', newPassword: '' });
  return (
    <Dialog open={open} onClose={onClose} className="dialog-backdrop--anchored" title="个人设置" description="查看账号信息或更新登录密码。">
      <div className="account-summary">
        <span className="account-summary__avatar">{user?.username?.slice(0, 1)?.toUpperCase() || 'U'}</span>
        <div><strong>{user?.username}</strong><span>{user?.role === 'admin' ? '管理员账号' : '普通账号'} · {Number(user?.points || 0).toFixed(2).replace(/\.00$/, '')} 积分</span></div>
      </div>
      <form className="settings-form" onSubmit={(event) => { event.preventDefault(); onSave(passwords); }}>
        <h3>修改密码</h3>
        <label><span>当前密码</span><input type="password" value={passwords.oldPassword} onChange={(event) => setPasswords((current) => ({ ...current, oldPassword: event.target.value }))} /></label>
        <label><span>新密码</span><input type="password" minLength={8} pattern="(?=.*[A-Za-z])(?=.*\d).{8,}" title="至少 8 位，并且必须同时包含字母和数字" value={passwords.newPassword} onChange={(event) => setPasswords((current) => ({ ...current, newPassword: event.target.value }))} /></label>
        <button className="secondary-button" type="submit" disabled={busy || !passwords.newPassword}><Save size={16} />保存新密码</button>
      </form>
      <button className="logout-command" type="button" onClick={onLogout}><LogOut size={17} />退出登录</button>
    </Dialog>
  );
}

export function DetailsDialog({ open, onClose, task }) {
  if (!task) return null;
  const isVideo = task.kind === 'video' || Boolean(task.videoUrl || task.duration);
  const prompt = task.promptOriginal || task.prompt || '未填写提示词';
  const fields = isVideo ? [
    ['模型', task.modelName || task.model || '-', Sparkles],
    ['画面比例', task.ratio || '-', Frame],
    ['清晰度', task.resolution || '720P', MonitorUp],
    ['视频时长', `${task.duration || '-'} 秒`, Clock3],
    ['参考图片', `${task.referenceCount || task.references?.length || 0} 张`, Image],
    ['创建时间', task.createdAt || '-', Clock3],
  ] : [
    ['模型', task.modelName || task.model || '-', Sparkles],
    ['清晰度', task.resolution || '-', MonitorUp],
    ['画面比例', task.ratio || '-', Frame],
    ['生成数量', `${task.count || task.images?.length || 0} 张`, Layers3],
    ['单张积分', `${task.unitCost ?? (task.count ? Number(task.cost || 0) / task.count : 0)} 积分`, Coins],
    ['消耗积分', `${task.cost || 0} 积分`, Coins],
    ['风格预设', task.stylePreset || '无', Sparkles],
    ['创建时间', task.createdAt || '-', Clock3],
  ];
  return (
    <Dialog open={open} onClose={onClose} title={isVideo ? '视频生成参数' : '图片生成参数'} description="任务提交时使用的完整配置。">
      <div className="parameter-dialog-layout">
        <section className="parameter-prompt-card"><span>完整提示词</span><p>{prompt}</p></section>
        <dl className="parameter-summary-grid">{fields.map(([label, value, Icon]) => <div key={label}><dt><Icon size={16} />{label}</dt><dd>{value}</dd></div>)}</dl>
      </div>
    </Dialog>
  );
}

export function GroupDialog({ open, onClose, task, onSave, busy }) {
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) setName(task?.groupName || '');
  }, [open, task?.groupName, task?.id]);

  return (
    <Dialog open={open} onClose={onClose} title="设置记录分组" description="给这批图片添加一个简短分组名称，便于后续整理和查找。">
      <form className="group-dialog-form" onSubmit={(event) => { event.preventDefault(); onSave(name.trim()); }}>
        <label htmlFor="task-group-name">分组名称</label>
        <div className="field-shell">
          <FolderPlus size={17} />
          <input
            id="task-group-name"
            value={name}
            maxLength={40}
            onChange={(event) => setName(event.target.value)}
            placeholder="例如：电商主图、角色设定"
            autoFocus
          />
        </div>
        <div className="group-dialog-actions">
          <button type="button" className="secondary-button danger-text" disabled={busy || !task?.groupName} onClick={() => onSave('')}>
            <Trash2 size={16} />清除分组
          </button>
          <button type="submit" className="primary-button" disabled={busy || name.trim() === (task?.groupName || '')}>
            <Save size={16} />保存分组
          </button>
        </div>
      </form>
    </Dialog>
  );
}
